import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";
import { toast } from "sonner";

export type AuthProfile = {
  avatar_url: string | null;
  avatar_url_200: string | null;
  avatar_url_80: string | null;
  display_name: string | null;
};

export type AuthContextValue = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  isReviewer: boolean;
  profile: AuthProfile | null;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
  validateSession: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isReviewer, setIsReviewer] = useState(false);
  const [profile, setProfile] = useState<AuthProfile | null>(null);

  const lastProfileUserIdRef = useRef<string | null>(null);

  const validateSession = useCallback(async () => {
    const {
      data: { session: currentSession },
      error,
    } = await supabase.auth.getSession();

    if (error) {
      console.error("❌ Error validating session:", error);
      return;
    }

    if (!currentSession && user) {
      console.error("❌ Session mismatch detected - forcing logout");
      setUser(null);
      setSession(null);
      setIsAdmin(false);
      setIsReviewer(false);
      setProfile(null);
      lastProfileUserIdRef.current = null;
      toast.error("Your session has expired. Please log in again.");
    }

    if (currentSession && !user) {
      console.warn("⚠️ Session exists but user state is null - resyncing");
      setUser(currentSession.user);
      setSession(currentSession);
    }
  }, [user]);

  useEffect(() => {
    const interval = setInterval(() => {
      validateSession();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [validateSession]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      console.log(`🔐 Auth Event: ${event}`, {
        hasSession: !!nextSession,
        emailVerified: nextSession?.user?.email_confirmed_at ? true : false,
        expiresAt: nextSession?.expires_at
          ? new Date(nextSession.expires_at * 1000).toLocaleString()
          : null,
        userId: nextSession?.user?.id,
      });

      // If user is signing in but email not verified, sign them out
      if (
        event === "SIGNED_IN" &&
        nextSession?.user &&
        !nextSession.user.email_confirmed_at
      ) {
        console.warn("⚠️ Unverified user detected, forcing sign out");
        setTimeout(() => {
          supabase.auth.signOut();
          toast.error("Email not confirmed - Please check your email");
        }, 100);
        return;
      }

      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      if (event === "SIGNED_OUT") {
        console.warn("⚠️ User signed out");
        setIsAdmin(false);
        setIsReviewer(false);
        setProfile(null);
        lastProfileUserIdRef.current = null;
        setLoading(false);
        return;
      }

      if (event === "TOKEN_REFRESHED" && nextSession) {
        console.log("✅ Token refreshed successfully");
        try {
          const jwt = nextSession.access_token;
          const payload = JSON.parse(atob(jwt.split(".")[1]));
          setIsAdmin(payload.is_admin || false);
          setIsReviewer(payload.is_reviewer || false);
        } catch (error) {
          console.error("Failed to decode refreshed JWT claims:", error);
        }
      }

      if (event === "SIGNED_IN") {
        console.log("✅ User signed in successfully");
      }

      if (nextSession?.user) {
        try {
          const jwt = nextSession.access_token;
          const payload = JSON.parse(atob(jwt.split(".")[1]));
          setIsAdmin(payload.is_admin || false);
          setIsReviewer(payload.is_reviewer || false);
          console.log("✅ Roles from JWT:", {
            isAdmin: payload.is_admin,
            isReviewer: payload.is_reviewer,
          });
        } catch (error) {
          console.error("Failed to decode JWT claims:", error);
          setIsAdmin(false);
          setIsReviewer(false);
        }

        // Fetch profile only when the user_id changes
        if (lastProfileUserIdRef.current !== nextSession.user.id) {
          lastProfileUserIdRef.current = nextSession.user.id;
          setTimeout(async () => {
            try {
              const { data: profileData, error: profileError } = await supabase
                .from("profiles")
                .select("avatar_url, avatar_url_200, avatar_url_80, display_name")
                .eq("user_id", nextSession.user.id)
                .single();

              if (!profileError && profileData) {
                setProfile(profileData);
              }
            } catch (error) {
              console.error("Failed to fetch profile:", error);
            } finally {
              setLoading(false);
            }
          }, 0);
        } else {
          setLoading(false);
        }
      } else {
        setIsAdmin(false);
        setIsReviewer(false);
        setProfile(null);
        lastProfileUserIdRef.current = null;
        setLoading(false);
      }
    });

    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      if (!initialSession) setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    try {
      console.log("🚪 User initiated logout");

      const {
        data: { session: currentSession },
      } = await supabase.auth.getSession();

      if (!currentSession) {
        console.log("⚠️ No active session found - cleaning up local state");
        setUser(null);
        setSession(null);
        setIsAdmin(false);
        setIsReviewer(false);
        setProfile(null);
        lastProfileUserIdRef.current = null;
        toast.success("Logged out successfully");
        return;
      }

      const { error } = await supabase.auth.signOut();

      // Clear session key so new anonymous session is created
      localStorage.removeItem("session_key");

      if (error) {
        if (error.message.includes("session") && error.message.includes("not found")) {
          console.warn("⚠️ Session already invalidated - cleaning up");
          setUser(null);
          setSession(null);
          setIsAdmin(false);
          setIsReviewer(false);
          setProfile(null);
          lastProfileUserIdRef.current = null;
          toast.success("Logged out successfully");
        } else {
          console.error("❌ Logout failed:", error);
          toast.error(`Logout failed: ${error.message}`);
        }
      } else {
        console.log("✅ Logged out successfully");
        toast.success("Logged out successfully");
      }
    } catch (err) {
      console.error("❌ Unexpected error during logout:", err);
      setUser(null);
      setSession(null);
      setIsAdmin(false);
      setIsReviewer(false);
      setProfile(null);
      lastProfileUserIdRef.current = null;
      toast.error("Logged out with errors");
    }
  };

  const refreshSession = async () => {
    try {
      console.log("🔄 Manually refreshing session");
      const { error } = await supabase.auth.refreshSession();
      if (error) {
        console.error("❌ Session refresh failed:", error);
        toast.error("Failed to refresh session");
        return false;
      }
      console.log("✅ Session refreshed manually");
      return true;
    } catch (err) {
      console.error("❌ Unexpected error refreshing session:", err);
      return false;
    }
  };

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      loading,
      isAdmin,
      isReviewer,
      profile,
      signOut,
      refreshSession,
      validateSession,
    }),
    [
      user,
      session,
      loading,
      isAdmin,
      isReviewer,
      profile,
      validateSession,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
